import {
  DEFAULT_PRIMARY_NAVIGATION_DESTINATIONS,
  PRIMARY_NAVIGATION_DESTINATION_OPTIONS,
  isHiddenPrimaryNavigationDestination,
  resolvePrimaryNavigationDestinations,
  resolveVisiblePrimaryNavigationDestinations,
  type PrimaryNavigationDestination,
} from "@-/dev.improve.haniverse.database.schemas.actor.schema._tables.user";

export type PrimaryNavigationDestinationGates = {
  /** Cookbook is experimental; off until Admin settings enables it. */
  isCookbookEnabled: boolean;
};

/**
 * Destinations this device may place in the strip, in their canonical (Settings palette) order.
 * Temporarily hidden destinations (Idioms, Collections) are never offered.
 */
export function getAvailablePrimaryNavigationDestinations({
  isCookbookEnabled,
}: PrimaryNavigationDestinationGates): PrimaryNavigationDestination[] {
  return PRIMARY_NAVIGATION_DESTINATION_OPTIONS.filter(
    (destination) =>
      !isHiddenPrimaryNavigationDestination(destination) &&
      (destination !== "cookbook" || isCookbookEnabled),
  );
}

/**
 * The strip's destinations in the user's order. A stored order that leaves nothing this device can
 * show (only gated or hidden destinations) falls back to the defaults, so the strip is never empty.
 */
export function resolvePrimaryNavigationStripDestinations(
  stored: unknown,
  available: readonly PrimaryNavigationDestination[],
): PrimaryNavigationDestination[] {
  const visible = resolveVisiblePrimaryNavigationDestinations(stored).filter((destination) =>
    available.includes(destination),
  );
  if (visible.length > 0) {
    return visible;
  }
  const defaults = DEFAULT_PRIMARY_NAVIGATION_DESTINATIONS.filter((destination) =>
    available.includes(destination),
  );
  return defaults.length > 0 ? defaults : available.slice(0, 1);
}

/**
 * The stored setting after the strip was edited to `edited`: that order first, then every stored
 * destination the editor could not offer (hidden, or gated on this device). A Cookbook tab
 * therefore survives an edit made while Cookbook is off, and returns when it is on again.
 */
export function mergeEditedPrimaryNavigationDestinations(
  stored: unknown,
  edited: readonly PrimaryNavigationDestination[],
  available: readonly PrimaryNavigationDestination[],
): PrimaryNavigationDestination[] {
  return [
    ...edited,
    ...resolvePrimaryNavigationDestinations(stored).filter(
      (destination) => !available.includes(destination) && !edited.includes(destination),
    ),
  ];
}

/** The default strip, limited to what this device can show. */
export function getDefaultPrimaryNavigationStripDestinations(
  available: readonly PrimaryNavigationDestination[],
): PrimaryNavigationDestination[] {
  return resolvePrimaryNavigationStripDestinations(
    DEFAULT_PRIMARY_NAVIGATION_DESTINATIONS,
    available,
  );
}
